import { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, FilterX, Download, Upload, Plus, Archive, Eye, X, ImageOff, ListTodo } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../i18n";
import { useUrlSearchParam } from "../hooks/useUrlSearchParam";
import { apiFetch } from "../api/apiFetch";
import { useWarehouseScope } from "../access/useWarehouseScope";
import {
    PPD_HEADER, PPD_CONSTANTS,
    ppdNum, downloadCsv, downloadXlsx,
} from "./helpers/exportFormats";
import { readXlsxRows } from "./helpers/xlsxRead";
import "../styles/manual-products-admin.css";

// Admin › Warehouse › Manual Products (plan: feat-manual-products.md).
//
// Two halves: the LISTS panel (named spreadsheets of products without
// barcodes — create, upload XLSX rows into, archive) and the SKU table (one
// row per label printed from SKU Finder › Manual Products) with the same
// Table / PPD / Jobalots exports as Wipe admin. The supplier filter is what
// lets one supplier's SKUs be exported on their own (Alex, decision 3).

const PAGE_SIZE = 25;
// BE caps page_size at 200 — the export pages through in these chunks.
const EXPORT_CHUNK = 200;
// Rows per upload request (BE cap is 2000).
const UPLOAD_CHUNK = 200;

export interface ManualRow {
    id: number;
    created_at: string;
    warehouse: string | null;
    sku: string;
    list_id: number | null;
    list_name: string | null;
    supplier: string | null;
    product_identifier: string | null;
    barcode: string | null;
    title: string | null;
    image_url: string | null;
    cost: number | string | null;
    rrp: number | string | null;
    weight_grams: number | string | null;
    processed_by_name: string | null;
    // Jobalots' verdict from the stock pushes (BE listingStatus.js): true =
    // listed, false = rejected (not on Jobalots), null = never pushed.
    jobalots_listed?: boolean | null;
    jobalots_checked_at?: string | null;
}

export interface ManualList {
    id: number;
    name: string;
    created_at: string;
    created_by_name: string | null;
    archived_at: string | null;
    item_count: number;
    sku_count: number;
}

export interface ManualItem {
    id: number;
    list_id: number;
    supplier: string;
    product_identifier: string;
    barcode: string | null;
    title: string;
    image_url: string | null;
    cost: number | string | null;
    rrp: number | string | null;
    weight_grams: number | string | null;
}

// ---- Upload column mapping ------------------------------------------------
// The template headers, in order. Uploaded files are matched on a relaxed
// form of the header (lower-case, letters and digits only), so "Product
// Identifier", "product_identifier" and "Product ID" all land on the same
// field. Supplier, Product Identifier and Title are required.
export const UPLOAD_COLUMNS = ["Supplier", "Product Identifier", "Barcode", "Title", "Image", "Cost", "RRP", "Weight (g)"] as const;
export type UploadField = "supplier" | "product_identifier" | "barcode" | "title" | "image_url" | "cost" | "rrp" | "weight_grams";
export const REQUIRED_FIELDS: UploadField[] = ["supplier", "product_identifier", "title"];
const HEADER_ALIASES: Record<string, UploadField> = {
    supplier: "supplier", vendor: "supplier", suppliername: "supplier",
    productidentifier: "product_identifier", identifier: "product_identifier", productid: "product_identifier", sku: "product_identifier", productsku: "product_identifier",
    barcode: "barcode", ean: "barcode", upc: "barcode", gtin: "barcode",
    title: "title", producttitle: "title", name: "title", productname: "title", description: "title",
    image: "image_url", imageurl: "image_url", imagelink: "image_url", picture: "image_url", photo: "image_url",
    cost: "cost", costprice: "cost", unitcost: "cost",
    rrp: "rrp", price: "rrp", sellprice: "rrp", retailprice: "rrp",
    weight: "weight_grams", weightg: "weight_grams", weightgrams: "weight_grams", weightingrams: "weight_grams",
};
const relaxHeader = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, "");

// Which upload field each sheet column feeds (null = ignored), plus the
// required fields the sheet does not provide.
export const mapUploadHeader = (header: string[]): { fields: (UploadField | null)[]; missing: UploadField[] } => {
    const fields = header.map(h => HEADER_ALIASES[relaxHeader(h)] ?? null);
    const present = new Set(fields.filter((f): f is UploadField => f !== null));
    return { fields, missing: REQUIRED_FIELDS.filter(f => !present.has(f)) };
};

// Sheet rows → the `{ rows }` body the BE upsert expects. Blank cells are
// omitted so the BE treats them as null.
export const rowsFromSheet = (fields: (UploadField | null)[], rows: string[][]): Record<string, string>[] =>
    rows.map(cells => {
        const out: Record<string, string> = {};
        fields.forEach((f, i) => {
            if (!f) return;
            const v = (cells[i] ?? "").trim();
            if (v !== "") out[f] = v;
        });
        return out;
    });

// ---- Export mappings ------------------------------------------------------
// PPD: the same external format Wipe exports (helpers/exportFormats.ts).
// Per-row columns come first so the supplier overrides the "Amazon LaaS"
// constants; weight and cost fall back the way Wipe does (980 g / 0).
export const manualPpdValue = (header: string, r: ManualRow): string | number => {
    switch (header) {
        case "EAN":
        case "EAN_channel(MasterKey)":
        case "EAN_owner":
            return r.sku ?? "";
        case "Product Title":
            return r.title ?? "";
        case "Product Description":
            return "";
        case "Product Supplier":
        case "Product Manufacturer":
            return r.supplier?.trim() || PPD_CONSTANTS[header];
        case "Product Weight (grams)": {
            const w = ppdNum(r.weight_grams, true);
            return w === "" ? 980 : w;
        }
        case "Bookon Cost":
        case "Product Cost": {
            const c = ppdNum(r.cost);
            return c === "" ? 0 : c;
        }
        case "Product RRP":
        case "Product Sell Price":
            return ppdNum(r.rrp);
        default:
            return Object.prototype.hasOwnProperty.call(PPD_CONSTANTS, header) ? PPD_CONSTANTS[header] : "";
    }
};

// The Jobalots import file is built server-side since 2026-10-31 (BE
// services/jobalotsExport.js, queued via POST /manual-products/jobalots-export)
// so its multi-language title column can be filled.

// Table export: identifier-style columns forced to Text (id, sku, product
// id, barcode) so Excel keeps digit strings verbatim.
const EXPORT_TEXT_COLUMNS = new Set<number>([0, 3, 6, 7]);

// created_at → DD/MM/YYYY (date only).
const formatDate = (iso: string) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};
const formatMoney = (v: number | string | null) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    return Number.isFinite(n) ? `£${n.toFixed(2)}` : "—";
};
const formatWeight = (v: number | string | null) => {
    if (v === null || v === undefined || v === "") return "—";
    const n = Number(v);
    return Number.isFinite(n) ? String(Math.round(n)) : "—";
};
const cell = (v: string | number | null | undefined) => (v === null || v === undefined || v === "" ? "—" : String(v));

const Thumb = ({ url, alt }: { url: string | null; alt: string }) => {
    const [broken, setBroken] = useState(false);
    useEffect(() => { setBroken(false); }, [url]);
    if (!url || broken) return <span className="manual-table-thumb manual-table-thumb--missing"><ImageOff size={16} /></span>;
    return <a href={url} target="_blank" rel="noreferrer"><img className="manual-table-thumb" src={url} alt={alt} loading="lazy" onError={() => setBroken(true)} /></a>;
};

const ManualProductsAdmin = () => {
    const { t } = useTranslation();

    // ---- Lists panel ----
    const [lists, setLists] = useState<ManualList[]>([]);
    const [listsLoading, setListsLoading] = useState(false);
    const [newListOpen, setNewListOpen] = useState(false);
    const [newListName, setNewListName] = useState("");
    const [uploadingListId, setUploadingListId] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const uploadTargetRef = useRef<number | null>(null);

    const loadLists = useCallback(async () => {
        setListsLoading(true);
        try {
            const res = await apiFetch("/manual-products/admin/lists", {});
            if (!res.ok) throw new Error(String(res.status));
            const data = await res.json();
            setLists(Array.isArray(data.lists) ? data.lists : []);
        } catch {
            toast.error(t("server_error"), { toastId: "manual-lists-err" });
        } finally {
            setListsLoading(false);
        }
    }, [t]);
    useEffect(() => { loadLists(); }, [loadLists]);

    const createList = async () => {
        const name = newListName.trim();
        if (!name) return;
        try {
            const res = await apiFetch("/manual-products/admin/lists", {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }),
            });
            if (res.status === 409) { toast.error(t("manual_products_list_exists"), { toastId: "manual-list-exists" }); return; }
            if (!res.ok) throw new Error(String(res.status));
            toast.success(t("manual_products_list_created"), { toastId: "manual-list-created" });
            setNewListName("");
            setNewListOpen(false);
            await loadLists();
        } catch {
            toast.error(t("server_error"), { toastId: "manual-list-create-err" });
        }
    };

    const archiveList = async (list: ManualList) => {
        if (!window.confirm(t("manual_products_archive_confirm"))) return;
        try {
            const res = await apiFetch(`/manual-products/admin/lists/${list.id}`, { method: "DELETE" });
            if (!res.ok) throw new Error(String(res.status));
            toast.success(t("manual_products_list_archived_done"), { toastId: "manual-list-archived" });
            await loadLists();
        } catch {
            toast.error(t("server_error"), { toastId: "manual-list-archive-err" });
        }
    };

    const downloadTemplate = async () => {
        await downloadXlsx("manual-products-template.xlsx", [...UPLOAD_COLUMNS], [], new Set([1, 2]), "Manual Products");
    };

    const pickFileFor = (listId: number) => {
        uploadTargetRef.current = listId;
        fileInputRef.current?.click();
    };

    const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        const listId = uploadTargetRef.current;
        if (!file || listId === null) return;
        setUploadingListId(listId);
        try {
            const { header, rows } = await readXlsxRows(file);
            const { fields, missing } = mapUploadHeader(header);
            if (missing.length > 0) {
                const names = missing.map(f => UPLOAD_COLUMNS[["supplier", "product_identifier", "barcode", "title", "image_url", "cost", "rrp", "weight_grams"].indexOf(f)]);
                toast.error(t("manual_products_upload_missing_columns", { columns: names.join(", ") }), { toastId: "manual-upload-cols" });
                return;
            }
            const shaped = rowsFromSheet(fields, rows);
            if (shaped.length === 0) { toast.error(t("manual_products_upload_empty"), { toastId: "manual-upload-empty" }); return; }

            let inserted = 0;
            let updated = 0;
            const errors: { row: number; message: string }[] = [];
            for (let i = 0; i < shaped.length; i += UPLOAD_CHUNK) {
                const chunk = shaped.slice(i, i + UPLOAD_CHUNK);
                const res = await apiFetch(`/manual-products/admin/lists/${listId}/items/bulk`, {
                    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ rows: chunk }),
                });
                const data = await res.json().catch(() => null);
                if (!res.ok) { toast.error(data?.message || t("manual_products_upload_failed"), { toastId: "manual-upload-err" }); return; }
                inserted += Number(data?.inserted ?? 0);
                updated += Number(data?.updated ?? 0);
                for (const err of (Array.isArray(data?.errors) ? data.errors : [])) {
                    // Row numbers are chunk-relative on the BE; make them sheet rows (header is row 1).
                    errors.push({ row: i + Number(err.row) + 1, message: String(err.message) });
                }
            }
            toast.success(t("manual_products_upload_done", { inserted: String(inserted), updated: String(updated) }), { toastId: "manual-upload-done" });
            if (errors.length > 0) {
                toast.warn(t("manual_products_upload_errors", { count: String(errors.length), row: String(errors[0].row), message: errors[0].message }), { toastId: "manual-upload-rejected", autoClose: 12000 });
            }
            await loadLists();
            refresh();
        } catch {
            toast.error(t("manual_products_upload_failed"), { toastId: "manual-upload-err" });
        } finally {
            setUploadingListId(null);
        }
    };

    // ---- Rows modal (one list's uploaded rows) ----
    const [rowsList, setRowsList] = useState<ManualList | null>(null);
    const [rowsItems, setRowsItems] = useState<ManualItem[]>([]);
    const [rowsTotal, setRowsTotal] = useState(0);
    const [rowsPage, setRowsPage] = useState(0);
    const [rowsQuery, setRowsQuery] = useState("");
    const [rowsLoading, setRowsLoading] = useState(false);
    const ROWS_PAGE = 50;

    useEffect(() => {
        if (!rowsList) return;
        let alive = true;
        setRowsLoading(true);
        const timer = setTimeout(async () => {
            try {
                const params = new URLSearchParams({ page: String(rowsPage), page_size: String(ROWS_PAGE) });
                if (rowsQuery.trim()) params.set("q", rowsQuery.trim());
                const res = await apiFetch(`/manual-products/admin/lists/${rowsList.id}/items?${params}`, {});
                if (!res.ok) throw new Error(String(res.status));
                const data = await res.json();
                if (!alive) return;
                setRowsItems(Array.isArray(data.items) ? data.items : []);
                setRowsTotal(typeof data.total === "number" ? data.total : 0);
            } catch {
                if (alive) toast.error(t("server_error"), { toastId: "manual-rows-err" });
            } finally {
                if (alive) setRowsLoading(false);
            }
        }, 200);
        return () => { alive = false; clearTimeout(timer); };
    }, [rowsList, rowsPage, rowsQuery, t]);

    const openRows = (list: ManualList) => { setRowsList(list); setRowsPage(0); setRowsQuery(""); setRowsItems([]); setRowsTotal(0); };
    const rowsPages = Math.max(1, Math.ceil(rowsTotal / ROWS_PAGE));

    // ---- SKU table ----
    const [rows, setRows] = useState<ManualRow[]>([]);
    const [total, setTotal] = useState(0);
    const [searchFilter, setSearchFilter] = useUrlSearchParam("search");
    const [searchInput, setSearchInput] = useState(searchFilter);
    useEffect(() => { setSearchInput(searchFilter); }, [searchFilter]);
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState("");
    // Without cross_warehouse the table is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [supplierFilter, setSupplierFilter] = useState("");
    const [listFilter, setListFilter] = useState("");
    // "" = all, "yes" = listed on Jobalots, "no" = confirmed rejected by Jobalots on its last stock push (never-pushed SKUs are in neither).
    const [listedFilter, setListedFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [allWarehouses, setWarehouses] = useState<string[]>([]);
    const warehouses = warehouseScope.limit(allWarehouses);
    const [suppliers, setSuppliers] = useState<string[]>([]);
    const [page, setPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => { setPage(0); }, [searchFilter, warehouseFilter, supplierFilter, listFilter, listedFilter, fromDate, toDate]);

    useEffect(() => {
        (async () => {
            try {
                const res = await apiFetch(`/settings`, {});
                if (!res.ok) return;
                const data: { warehouse: string }[] = await res.json();
                setWarehouses(data.map(r => r.warehouse).filter(Boolean).sort());
            } catch { /* leave empty */ }
        })();
    }, []);

    const loadSuppliers = useCallback(async () => {
        try {
            const res = await apiFetch(`/manual-products/suppliers`, {});
            if (!res.ok) return;
            const data = await res.json();
            setSuppliers(Array.isArray(data.suppliers) ? data.suppliers : []);
        } catch { /* leave empty */ }
    }, []);
    useEffect(() => { loadSuppliers(); }, [loadSuppliers]);

    const buildFilterParams = useCallback(() => {
        const params = new URLSearchParams();
        if (searchFilter.trim()) params.set("q", searchFilter.trim());
        if (warehouseFilter) params.set("warehouse", warehouseFilter);
        if (supplierFilter) params.set("supplier", supplierFilter);
        if (listFilter) params.set("list_id", listFilter);
        if (listedFilter) params.set("listed", listedFilter);
        if (fromDate) params.set("from", fromDate);
        if (toDate) params.set("to", toDate);
        return params;
    }, [searchFilter, warehouseFilter, supplierFilter, listFilter, listedFilter, fromDate, toDate]);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildFilterParams();
            params.set("page", String(page));
            params.set("page_size", String(PAGE_SIZE));
            const res = await apiFetch(`/manual-products/list?${params}`, {});
            if (res.ok) {
                const data = await res.json();
                setRows(Array.isArray(data.products) ? data.products : []);
                setTotal(typeof data.total === "number" ? data.total : 0);
            }
        } catch {
            toast.error(t("server_error"), { toastId: "manual-load-err" });
        } finally {
            setLoading(false);
        }
    }, [buildFilterParams, page, t]);
    useEffect(() => { refresh(); }, [refresh]);

    const hasActiveFilters = Boolean(searchFilter.trim() || pickedWarehouse || supplierFilter || listFilter || listedFilter || fromDate || toDate);
    const clearFilters = () => {
        setSearchFilter(""); setSearchInput(""); setWarehouseFilter(""); setSupplierFilter(""); setListFilter(""); setListedFilter(""); setFromDate(""); setToDate("");
    };

    // The Jobalots column: the verdict of the last stock push for the SKU.
    const jobalotsCell = (r: ManualRow) => {
        const label = r.jobalots_listed === true ? t("wipe_jobalots_listed")
            : r.jobalots_listed === false ? t("wipe_jobalots_not_listed")
                : t("wipe_jobalots_not_checked");
        const tone = r.jobalots_listed === true ? "wipe-jobalots--listed" : r.jobalots_listed === false ? "wipe-jobalots--not-listed" : "wipe-jobalots--unknown";
        const title = r.jobalots_checked_at ? t("wipe_jobalots_checked_at", { date: formatDate(r.jobalots_checked_at) }) : undefined;
        return <td className={`wipe-jobalots ${tone}`} title={title}>{label}</td>;
    };

    // Collect EVERY row matching the current filters, paging through the BE cap.
    const fetchAllFiltered = async (): Promise<ManualRow[]> => {
        const all: ManualRow[] = [];
        let exportPage = 0;
        let expected = Infinity;
        do {
            const params = buildFilterParams();
            params.set("page", String(exportPage));
            params.set("page_size", String(EXPORT_CHUNK));
            const res = await apiFetch(`/manual-products/list?${params}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const batch: ManualRow[] = Array.isArray(data.products) ? data.products : [];
            all.push(...batch);
            expected = typeof data.total === "number" ? data.total : all.length;
            exportPage += 1;
            if (batch.length === 0) break;
        } while (all.length < expected);
        return all;
    };

    const exportTable = async () => {
        setDownloading(true);
        try {
            const all = await fetchAllFiltered();
            const header = [
                t("manual_products_col_id"), t("manual_products_col_date"), t("manual_products_col_warehouse"),
                t("manual_products_col_sku"), t("manual_products_col_list"), t("manual_products_col_supplier"),
                t("manual_products_col_identifier"), t("manual_products_col_barcode"), t("manual_products_col_title"),
                t("manual_products_col_image"), t("manual_products_col_cost"), t("manual_products_col_rrp"),
                t("manual_products_col_weight"), t("manual_products_col_by"),
            ];
            const out: (string | number)[][] = all.map(r => [
                String(r.id), formatDate(r.created_at), r.warehouse ?? "", r.sku ?? "", r.list_name ?? "", r.supplier ?? "",
                r.product_identifier ?? "", r.barcode ?? "", r.title ?? "", r.image_url ?? "",
                ppdNum(r.cost), ppdNum(r.rrp), ppdNum(r.weight_grams, true), r.processed_by_name ?? "",
            ]);
            await downloadXlsx("manual-products.xlsx", header, out, EXPORT_TEXT_COLUMNS, "Manual Products");
        } catch {
            toast.error(t("server_error"), { toastId: "manual-export-err" });
        } finally {
            setDownloading(false);
        }
    };

    const ppdExport = async () => {
        setDownloading(true);
        try {
            const all = await fetchAllFiltered();
            const out = all.map(r => PPD_HEADER.map(h => manualPpdValue(h, r)));
            downloadCsv("ppd-export.csv", [[...PPD_HEADER], ...out]);
        } catch {
            toast.error(t("server_error"), { toastId: "manual-ppd-err" });
        } finally {
            setDownloading(false);
        }
    };

    // Jobalots export — queued server-side with the current filters (same
    // query string as the list) and built by the jobs runner, which fills
    // the multi-language title column; the file appears under Admin ›
    // Warehouse › Reports › Queued exports.
    const jobalotsExport = async () => {
        setDownloading(true);
        try {
            const res = await apiFetch(`/manual-products/jobalots-export?${buildFilterParams()}`, { method: "POST" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            toast.success(t("wipe_jobalots_queued"), { toastId: "manual-jobalots-queued" });
        } catch {
            toast.error(t("wipe_jobalots_queue_failed"), { toastId: "manual-jobalots-err" });
        } finally {
            setDownloading(false);
        }
    };

    // Sync Jobalots — re-push stock for every SKU in the current filters
    // whose Jobalots status is not Listed; each reply refreshes the column.
    const jobalotsResync = async () => {
        setDownloading(true);
        try {
            const res = await apiFetch(`/manual-products/jobalots-resync?${buildFilterParams()}`, { method: "POST" });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            const count = Number(data?.total) || 0;
            if (count === 0) toast.info(t("wipe_jobalots_sync_none"), { toastId: "manual-jobalots-sync" });
            else toast.success(t("wipe_jobalots_sync_started", { count: count.toLocaleString("en-GB") }), { toastId: "manual-jobalots-sync" });
        } catch {
            toast.error(t("wipe_jobalots_sync_failed"), { toastId: "manual-jobalots-sync-err" });
        } finally {
            setDownloading(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const rangeFrom = total === 0 ? 0 : safePage * PAGE_SIZE + 1;
    const rangeTo = Math.min((safePage + 1) * PAGE_SIZE, total);

    return (
        <div className="inventory-admin">
            <input ref={fileInputRef} type="file" accept=".xlsx" style={{ display: "none" }} onChange={onFileChosen} data-testid="manual-upload-input" />

            {rowsList && (
                <div className="manual-rows-overlay" role="dialog" aria-modal="true" aria-label={t("manual_products_rows_title", { name: rowsList.name })}>
                    <div className="manual-rows-modal">
                        <div className="manual-rows-head">
                            <h3>{t("manual_products_rows_title", { name: rowsList.name })}</h3>
                            <input
                                type="search"
                                placeholder={t("manual_products_rows_search")}
                                value={rowsQuery}
                                onChange={e => { setRowsQuery(e.target.value); setRowsPage(0); }}
                            />
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRowsList(null)} aria-label={t("close")}><X size={16} /></button>
                        </div>
                        <div className="manual-rows-body">
                            <table className="locations-table">
                                <thead>
                                    <tr>
                                        <th>{t("manual_products_col_image")}</th>
                                        <th>{t("manual_products_col_supplier")}</th>
                                        <th>{t("manual_products_col_identifier")}</th>
                                        <th>{t("manual_products_col_barcode")}</th>
                                        <th>{t("manual_products_col_title")}</th>
                                        <th className="locations-table-num">{t("manual_products_col_cost")}</th>
                                        <th className="locations-table-num">{t("manual_products_col_rrp")}</th>
                                        <th className="locations-table-num">{t("manual_products_col_weight")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rowsItems.map(i => (
                                        <tr key={i.id}>
                                            <td><Thumb url={i.image_url} alt={i.title} /></td>
                                            <td>{cell(i.supplier)}</td>
                                            <td>{cell(i.product_identifier)}</td>
                                            <td>{cell(i.barcode)}</td>
                                            <td title={i.title}>{cell(i.title)}</td>
                                            <td className="locations-table-num">{formatMoney(i.cost)}</td>
                                            <td className="locations-table-num">{formatMoney(i.rrp)}</td>
                                            <td className="locations-table-num">{formatWeight(i.weight_grams)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {!rowsLoading && rowsTotal === 0 && <div className="locations-table-empty">{t("manual_products_rows_empty")}</div>}
                        </div>
                        <div className="manual-rows-foot">
                            <span>{t("locations_showing_range", {
                                from: String(rowsTotal === 0 ? 0 : rowsPage * ROWS_PAGE + 1),
                                to: String(Math.min((rowsPage + 1) * ROWS_PAGE, rowsTotal)),
                                total: String(rowsTotal),
                            })}</span>
                            <div className="manual-rows-pages">
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRowsPage(p => Math.max(0, p - 1))} disabled={rowsPage === 0} aria-label={t("pagination_previous")}><ChevronLeft size={14} /></button>
                                <span>{t("pagination_page_x_of_y", { page: String(rowsPage + 1), total: String(rowsPages) })}</span>
                                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setRowsPage(p => Math.min(rowsPages - 1, p + 1))} disabled={rowsPage >= rowsPages - 1} aria-label={t("pagination_next")}><ChevronRight size={14} /></button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <section className="manual-lists" aria-label={t("manual_products_lists_title")}>
                <div className="manual-lists-head">
                    <h3>{t("manual_products_lists_title")}</h3>
                    <span className="manual-lists-spacer" />
                    <button type="button" className="btn btn-ghost btn-sm" onClick={downloadTemplate}>
                        <Download size={14} /> {t("manual_products_template")}
                    </button>
                    <button type="button" className="btn btn-primary btn-sm" onClick={() => setNewListOpen(o => !o)}>
                        <Plus size={14} /> {t("manual_products_new_list")}
                    </button>
                </div>
                {newListOpen && (
                    <form className="manual-new-list" onSubmit={e => { e.preventDefault(); createList(); }}>
                        <input
                            autoFocus
                            placeholder={t("manual_products_list_name_placeholder")}
                            aria-label={t("manual_products_list_name_placeholder")}
                            value={newListName}
                            maxLength={120}
                            onChange={e => setNewListName(e.target.value)}
                        />
                        <button type="submit" className="btn btn-secondary btn-sm" disabled={!newListName.trim()}>{t("manual_products_create_list")}</button>
                        <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setNewListOpen(false); setNewListName(""); }}>{t("close")}</button>
                    </form>
                )}
                {lists.length === 0 && !listsLoading && (
                    <div className="manual-lists-empty">{t("manual_products_no_lists")}</div>
                )}
                {lists.length > 0 && (
                    <table className="manual-lists-table">
                        <thead>
                            <tr>
                                <th>{t("manual_products_col_list")}</th>
                                <th>{t("manual_products_col_date")}</th>
                                <th>{t("manual_products_col_by")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {lists.map(l => (
                                <tr key={l.id} className={l.archived_at ? "is-archived" : ""}>
                                    <td>
                                        <strong>{l.name}</strong>
                                        <span className="manual-list-badge">{t("manual_products_rows", { count: String(l.item_count) })}</span>
                                        <span className="manual-list-badge">{t("manual_products_skus", { count: String(l.sku_count) })}</span>
                                        {l.archived_at && <span className="manual-list-badge">{t("manual_products_archived")}</span>}
                                    </td>
                                    <td>{formatDate(l.created_at)}</td>
                                    <td>{cell(l.created_by_name)}</td>
                                    <td>
                                        <div className="manual-list-actions">
                                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => openRows(l)}>
                                                <Eye size={14} /> {t("manual_products_view_rows")}
                                            </button>
                                            {!l.archived_at && (
                                                <>
                                                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => pickFileFor(l.id)} disabled={uploadingListId !== null}>
                                                        <Upload size={14} /> {uploadingListId === l.id ? t("submitting") : t("manual_products_upload")}
                                                    </button>
                                                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => archiveList(l)}>
                                                        <Archive size={14} /> {t("manual_products_archive")}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <div className="totes-admin-actions">
                <button type="button" className="btn btn-ghost" onClick={exportTable} disabled={loading || downloading || total === 0}>
                    <Download size={16} /> {downloading ? t("submitting") : t("wipe_export_table")}
                </button>
                <button type="button" className="btn btn-ghost" onClick={ppdExport} disabled={loading || downloading || total === 0}>
                    <Download size={16} /> {t("wipe_ppd_export")}
                </button>
                <button type="button" className="btn btn-ghost" onClick={jobalotsExport} disabled={loading || downloading || total === 0}>
                    <ListTodo size={16} /> {t("wipe_jobalots_queue")}
                </button>
                <button type="button" className="btn btn-ghost" onClick={jobalotsResync} disabled={loading || downloading || total === 0} title={t("wipe_jobalots_sync_hint")}>
                    <RefreshCw size={16} /> {t("wipe_jobalots_sync")}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters} disabled={!hasActiveFilters} style={{ marginLeft: "auto" }}>
                    <FilterX size={14} /> {t("wipe_clear_filters")}
                </button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => { refresh(); loadLists(); loadSuppliers(); }} disabled={loading}>
                    <RefreshCw size={14} /> {t("refresh")}
                </button>
            </div>

            <div className="warehouse-admin-filters users-admin-filters">
                <input
                    className="locations-table-search wh-admin-filter-search"
                    type="search"
                    placeholder={t("manual_products_filter_search_hint")}
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); setSearchFilter(searchInput.trim()); } }}
                    onBlur={() => { if (searchInput.trim() !== searchFilter.trim()) setSearchFilter(searchInput.trim()); }}
                />
                <select
                    className="wh-admin-filter-select"
                    value={warehouseFilter}
                    onChange={e => setWarehouseFilter(e.target.value)}
                    disabled={!warehouseScope.canCross}
                    aria-label={t("manual_products_col_warehouse")}
                >
                    {warehouseScope.canCross && <option value="">{t("manual_products_filter_all_warehouses")}</option>}
                    {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                </select>
                <select className="wh-admin-filter-select" value={supplierFilter} onChange={e => setSupplierFilter(e.target.value)} aria-label={t("manual_products_col_supplier")}>
                    <option value="">{t("manual_products_filter_all_suppliers")}</option>
                    {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="wh-admin-filter-select" value={listFilter} onChange={e => setListFilter(e.target.value)} aria-label={t("manual_products_col_list")}>
                    <option value="">{t("manual_products_filter_all_lists")}</option>
                    {lists.map(l => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
                </select>
                <select className="wh-admin-filter-select" value={listedFilter} onChange={e => setListedFilter(e.target.value)} aria-label={t("wipe_col_jobalots")}>
                    <option value="">{t("wipe_filter_all_listing")}</option>
                    <option value="yes">{t("wipe_filter_listed")}</option>
                    <option value="no">{t("wipe_filter_not_listed")}</option>
                </select>
                <div className="wipe-date-filters">
                    <label className="wipe-date-field">
                        <span className="wipe-date-label">{t("wipe_filter_from")}</span>
                        <input className="wh-admin-filter-date" type="date" value={fromDate} max={toDate || undefined} onChange={e => setFromDate(e.target.value)} />
                    </label>
                    <label className="wipe-date-field">
                        <span className="wipe-date-label">{t("wipe_filter_to")}</span>
                        <input className="wh-admin-filter-date" type="date" value={toDate} min={fromDate || undefined} onChange={e => setToDate(e.target.value)} />
                    </label>
                </div>
            </div>

            <div className="locations-table-wrap">
                <table className="locations-table wipe-admin-table">
                    <thead>
                        <tr>
                            <th className="locations-table-num">{t("manual_products_col_id")}</th>
                            <th>{t("manual_products_col_date")}</th>
                            <th>{t("manual_products_col_warehouse")}</th>
                            <th>{t("manual_products_col_sku")}</th>
                            <th>{t("manual_products_col_image")}</th>
                            <th>{t("manual_products_col_supplier")}</th>
                            <th>{t("manual_products_col_identifier")}</th>
                            <th>{t("manual_products_col_barcode")}</th>
                            <th>{t("manual_products_col_title")}</th>
                            <th>{t("manual_products_col_list")}</th>
                            <th>{t("wipe_col_jobalots")}</th>
                            <th className="locations-table-num">{t("manual_products_col_cost")}</th>
                            <th className="locations-table-num">{t("manual_products_col_rrp")}</th>
                            <th className="locations-table-num">{t("manual_products_col_weight")}</th>
                            <th>{t("manual_products_col_by")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => (
                            <tr key={r.id}>
                                <td className="locations-table-num">{r.id}</td>
                                <td>{formatDate(r.created_at)}</td>
                                <td>{cell(r.warehouse)}</td>
                                <td title={r.sku}>{cell(r.sku)}</td>
                                <td><Thumb url={r.image_url} alt={r.title ?? r.sku} /></td>
                                <td title={r.supplier ?? undefined}>{cell(r.supplier)}</td>
                                <td title={r.product_identifier ?? undefined}>{cell(r.product_identifier)}</td>
                                <td title={r.barcode ?? undefined}>{cell(r.barcode)}</td>
                                <td title={r.title ?? undefined}>{cell(r.title)}</td>
                                <td>{cell(r.list_name)}</td>
                                {jobalotsCell(r)}
                                <td className="locations-table-num">{formatMoney(r.cost)}</td>
                                <td className="locations-table-num">{formatMoney(r.rrp)}</td>
                                <td className="locations-table-num">{formatWeight(r.weight_grams)}</td>
                                <td>{cell(r.processed_by_name)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {!loading && total === 0 && <div className="locations-table-empty">{t("manual_products_no_rows")}</div>}

            {total > 0 && (
                <div className="locations-table-footer">
                    <span>{t("locations_showing_range", { from: String(rangeFrom), to: String(rangeTo), total: String(total) })}</span>
                    <div className="locations-table-pagination">
                        <button type="button" className="locations-table-page-btn" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0} aria-label={t("pagination_previous")}>
                            <ChevronLeft size={16} />
                            <span>{t("pagination_previous")}</span>
                        </button>
                        <span className="locations-table-page-indicator">
                            {t("pagination_page_x_of_y", { page: String(safePage + 1), total: String(totalPages) })}
                        </span>
                        <button type="button" className="locations-table-page-btn" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={safePage >= totalPages - 1} aria-label={t("pagination_next")}>
                            <span>{t("pagination_next")}</span>
                            <ChevronRight size={16} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManualProductsAdmin;
